import React, { FC, useState, useEffect } from 'react';
import { Box, Stack, Button, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DefaultCustomField, Message, MessageTemplate } from 'src/constants/types';
import { convertHtmlToText } from 'src/helpers/helpers';
import { CampaignListItem } from '../../types';
import { getAwayRecurringMessagesCampaign } from '../../slices';
import { useSelector } from 'react-redux';
import TemplateText from 'src/components/TemplateText/TemplateText';
import { MessageCreditEstimates } from 'src/components/MessageCreditEstimates';

interface CustomMessageFormProps {
  companyStatus: 'available' | 'away';
  message?: Message;
  awayMessageCampaign: CampaignListItem;
  customFields: DefaultCustomField[];
  messageTemplates: MessageTemplate[];
  submitSaveCustomAwayMessage: (
    awayMessageCampaign: CampaignListItem,
    awayRecurringMessageCampaign: CampaignListItem,
    text: string,
    shoudActivateCampaign: boolean,
  ) => Promise<void>;
  setIsCustomFormDirty: React.Dispatch<React.SetStateAction<boolean>>;
  shouldActivateCampaign: boolean
}

export const CustomMessageForm: FC<CustomMessageFormProps> = ({
  message,
  awayMessageCampaign,
  customFields,
  messageTemplates,
  submitSaveCustomAwayMessage,
  setIsCustomFormDirty,
  shouldActivateCampaign,
}) => {
  const initialText = message ? convertHtmlToText(message.text) : '';
  const [text, setText] = useState<string>(initialText);

  const [originalText] = useState(initialText);
  const [isMessageDeleted, setIsMessageDeleted] = useState<boolean>(false);

  const awayRecurringMessageCampaign = useSelector(getAwayRecurringMessagesCampaign);

  const handleClearText = () => {
    setText('');
  };

  const handleUpdateText = (newText: string | undefined) => {
    setText(convertHtmlToText(newText ?? ''));
  };

  const isTextEmpty = text.trim().length === 0;
  const isUnchanged = text === originalText;

  const isButtonDisabled = (isTextEmpty && !awayMessageCampaign.id) || isUnchanged;

  useEffect(() => {
    setIsCustomFormDirty(text !== originalText);
  }, [text, originalText, setIsCustomFormDirty]);

  return (
    <Box sx={{ position: 'relative', marginTop: '10px' }}>
      {!isMessageDeleted && (
        <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="caption" fontStyle="italic" mb={0.625}>
              NOTE: Custom away messages take priority over scheduled and recurring away messages.
            </Typography>
          </Box>
          <Box>
            <Button
              sx={{ minWidth: '10px' }}
              size="small"
              onClick={e => {
                e.stopPropagation();
                setText('');
                setIsMessageDeleted(true);
                setIsCustomFormDirty(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </Button>
          </Box>
        </Box>
      )}
      {!isMessageDeleted && (
        <TemplateText
          showConfirmationText={false}
          confirmationText=""
          setFocus
          clearText={() => {
            handleClearText();
            setIsCustomFormDirty(true);
          }}
          customFields={customFields}
          emodjiPickerPosition={{ top: '0', right: '0' }}
          messageTemplates={messageTemplates}
          text={text}
          updateText={handleUpdateText}
          note={
            <MessageCreditEstimates
              viewType="campaign"
              messageType="sms"
              messageText={text}
              hasAttachments={false}
              sx={{ py: 0.625, px: 1.25 }}
            />
          }
        />
      )}
      {isMessageDeleted && (
        <Stack direction="row">
          <Button
            variant="outlined"
            style={{ width: '100%', marginTop: '20px' }}
            onClick={() => {
              setIsMessageDeleted(false);
            }}
          >
            Add Message
          </Button>
        </Stack>
      )}
      <Stack direction="row" justifyContent="end">
        <Button
          disabled={isButtonDisabled}
          variant="contained"
          fullWidth
          sx={{ marginTop: '20px' }}
          onClick={() =>
            submitSaveCustomAwayMessage(
              awayMessageCampaign,
              awayRecurringMessageCampaign!,
              text,
              shouldActivateCampaign,
            )
          }
        >
          {awayMessageCampaign.id && awayMessageCampaign.scheduledMessages[0].text ? 'Save' : 'Create'}
        </Button>
      </Stack>
    </Box>
  );
};
